import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';
import { onAnimationEnd } from '@theme/utilities';
import { trapFocus, removeTrapFocus } from '@theme/focus';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  #pointerStartedOutside = false;
  #outsideFocusReleased = false;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #outsideFocusTimer;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    document.addEventListener('pointerdown', this.#handleOutsidePointerDown, true);
    document.addEventListener('pointerup', this.#restoreOutsideFocus, true);
    document.addEventListener('pointercancel', this.#restoreOutsideFocus, true);
    document.addEventListener('click', this.#handleOutsideClick, true);
    window.addEventListener('pageshow', this.#handlePageShow);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    document.removeEventListener('pointerdown', this.#handleOutsidePointerDown, true);
    document.removeEventListener('pointerup', this.#restoreOutsideFocus, true);
    document.removeEventListener('pointercancel', this.#restoreOutsideFocus, true);
    document.removeEventListener('click', this.#handleOutsideClick, true);
    window.removeEventListener('pageshow', this.#handlePageShow);
    this.#pointerStartedOutside = false;
    clearTimeout(this.#outsideFocusTimer);
  }

  /**
   * Recomputes the sticky summary after a back/forward cache restore re-renders the cart items.
   *
   * On viewports at or above 990px an open drawer survives a navigation and is reopened during
   * parsing (see the restore script in `snippets/theme-drawer.liquid`), so no open event fires
   * on the way back and the measurement taken before the shopper left is the one still applied.
   * An empty drawer measured as unpinned would keep the checkout CTA scrolling away once the
   * restore fills it with line items.
   */
  #handleCartSectionRestored = () => {
    requestAnimationFrame(() => this.#updateStickyState());
  };

  /** @param {MouseEvent} event */
  #canDismissOutside(event) {
    const drawer = this.#themeDrawer;
    const panel = this.#dialog;
    if (!drawer?.isOpen || !panel?.open || event.button !== 0) return false;
    if (event.composedPath().includes(panel)) return false;
    if (event.composedPath().some((node) => node instanceof Element && node.getAttribute('aria-controls') === drawer.id)) {
      return false;
    }
    if (
      document.querySelector('dialog:modal, [popover]:popover-open:not(#PBarNextFrameWrapper)') ||
      panel.querySelector('dialog[open]')
    ) {
      return false;
    }
    const order = Number(drawer.style.getPropertyValue('--drawer-stack-order') || 0);
    for (const other of document.querySelectorAll('theme-drawer[open]')) {
      if (other instanceof HTMLElement && Number(other.style.getPropertyValue('--drawer-stack-order') || 0) > order) {
        return false;
      }
    }
    return true;
  }

  /**
   * A direct Quick Add is allowed to update an already-open desktop cart without
   * dismissing and reopening it. The focus trap is still released for the
   * pointer sequence so the outside product-card button can receive the click.
   * Option-selection buttons keep the normal outside-click behavior because
   * they open a separate dialog and use the native dialog handoff lifecycle.
   *
   * @param {Event} event
   */
  #isDirectQuickAdd(event) {
    return event
      .composedPath()
      .some((node) => node instanceof Element && node.matches('.quick-add__button--add'));
  }

  /** @param {PointerEvent} event */
  #handleOutsidePointerDown = (event) => {
    const startedOutside = this.#canDismissOutside(event);
    const keepOpenForQuickAdd = startedOutside && this.#isDirectQuickAdd(event);
    this.#pointerStartedOutside = startedOutside && !keepOpenForQuickAdd;
    if (!startedOutside) return;
    removeTrapFocus();
    this.#outsideFocusReleased = true;
    clearTimeout(this.#outsideFocusTimer);
  };

  #restoreOutsideFocus = () => {
    if (!this.#outsideFocusReleased) return;
    clearTimeout(this.#outsideFocusTimer);
    this.#outsideFocusTimer = setTimeout(() => {
      this.#outsideFocusReleased = false;
      this.#pointerStartedOutside = false;
      const panel = this.#dialog;
      if (this.isConnected && this.#themeDrawer?.isOpen && panel?.open &&
          !document.querySelector('dialog:modal, theme-drawer[open]:not(#cart-drawer)')) {
        trapFocus(panel);
      }
    }, 0);
  };

  /** @param {MouseEvent} event */
  #handleOutsideClick = (event) => {
    const startedOutside = this.#pointerStartedOutside;
    this.#pointerStartedOutside = false;
    if (!startedOutside || !this.#canDismissOutside(event)) return;
    const activatesControl = event.composedPath().some(
      (node) => node instanceof Element && node.matches('a[href], button, input, select, textarea, summary, [role="button"], [role="link"], [tabindex]:not([tabindex="-1"])')
    );
    this.#themeDrawer?.close({ restoreFocus: !activatesControl });
  };

  /** @param {PageTransitionEvent} event */
  #handlePageShow = (event) => {
    if (event.persisted) this.#themeDrawer?.close();
  };

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );
    const sourceHeaderDrawer = /** @type {(HTMLElement & {isOpen?: boolean, close?: () => void}) | null} */ (
      event.target instanceof Element ? event.target.closest('header-drawer') : null
    );

    if (shouldAutoOpen && !sourceModal && !sourceHeaderDrawer?.isOpen && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(async ({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        if (sourceHeaderDrawer?.isOpen) {
          sourceHeaderDrawer.close?.();
          await onAnimationEnd(sourceHeaderDrawer.querySelector('.menu-drawer'), undefined, { subtree: false });
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
